import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { getLeagueAssetUrl, getLeagueIdByTrophies, getTownHallAssetUrl } from "@acme/coc";
import { api } from "~/common/utils/trpc.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@acme/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@acme/ui/dropdown-menu";
import { Button } from "@acme/ui/button";
import { Card, CardContent, CardHeader } from "@acme/ui/card";
import { MoreHorizontal, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { AddPlayerDialogV2 } from "~/common/components/add-player-dialog-v2.js";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useListViewMode } from "~/common/hooks/use-list-view-mode.js";
import { PlayerOverviewCard } from "./_components/-player-overview-card.js";
import { ViewToggle } from "./_components/-view-toggle.js";

export const Route = createFileRoute("/_authed/my-players/")({
  component: RouteComponent,
});

function LoadingSkeletonTable()
{
  return (
    <div className="rounded-md border border-input overflow-x-auto bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Town Hall</TableHead>
            <TableHead>Trophies</TableHead>
            <TableHead>Best Trophies</TableHead>
            <TableHead>Exp. Level</TableHead>
            <TableHead>War Stars</TableHead>
            <TableHead>War League Stars</TableHead>
            <TableHead>Clan Game Points</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 5 }).map((_, i) => (
            <TableRow key={i}>
              <TableCell>
                <div className="h-5 w-32 rounded bg-muted animate-pulse" />
              </TableCell>
              <TableCell>
                <div className="h-5 w-8 rounded bg-muted animate-pulse" />
              </TableCell>
              <TableCell>
                <div className="h-5 w-12 rounded bg-muted animate-pulse" />
              </TableCell>
              <TableCell>
                <div className="h-5 w-12 rounded bg-muted animate-pulse" />
              </TableCell>
              <TableCell>
                <div className="h-5 w-10 rounded bg-muted animate-pulse" />
              </TableCell>
              <TableCell>
                <div className="h-5 w-10 rounded bg-muted animate-pulse" />
              </TableCell>
              <TableCell>
                <div className="h-5 w-14 rounded bg-muted animate-pulse" />
              </TableCell>
              <TableCell>
                <div className="h-5 w-12 rounded bg-muted animate-pulse" />
              </TableCell>
              <TableCell className="text-right">
                <div className="h-8 w-8 rounded bg-muted animate-pulse ml-auto" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function LoadingSkeletonCards()
{
  return (
    <div
      className="grid gap-4"
      style={{
        gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 20rem), 1fr))",
      }}>
      {Array.from({ length: 6 }).map((_, i) => (
        <Card key={i} className="overflow-hidden">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded bg-muted animate-pulse" />
              <div className="h-5 w-32 rounded bg-muted animate-pulse" />
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-2 pt-0">
            {Array.from({ length: 6 }).map((_, j) => (
              <div key={j} className="h-4 w-full rounded bg-muted animate-pulse" />
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function RouteComponent()
{
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { effectiveView, viewMode, setViewMode, isBelowLg } = useListViewMode();
  const playersQueryOptions = api.player.getAllForOverviewByUserId.queryOptions();
  const { data: players, isPending: isLoading } = useQuery(playersQueryOptions);
  const [addPlayerDialogOpen, setAddPlayerDialogOpen] = useState(false);

  const unlinkFromUserMutation = useMutation(
    api.player.unlinkFromUser.mutationOptions({
      onSuccess: () =>
      {
        void queryClient.invalidateQueries({ queryKey: api.player.getAllForOverviewByUserId.queryKey() });
      },
    })
  );

  const handleUnlinkFromUser = (playerTag: string) =>
  {
    unlinkFromUserMutation.mutate({ playerTag });
  };

  const handleRowClick = (playerTag: string) =>
  {
    const tagNormalized = playerTag.replace(/^#/, "");
    void navigate({ to: "/my-players/$tag", params: { tag: tagNormalized } });
  };

  if (isLoading)
  {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="flex items-center justify-between mb-4 gap-2">
          <div className="h-9 w-48 rounded-md bg-muted animate-pulse" />
          <div className="flex gap-2">
            <ViewToggle viewMode={viewMode} setViewMode={setViewMode} isBelowLg={isBelowLg} />
            <div className="h-9 w-28 rounded-md bg-muted animate-pulse" />
          </div>
        </div>
        {effectiveView === "table" ? <LoadingSkeletonTable /> : <LoadingSkeletonCards />}
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h1 className="text-3xl font-bold">My Players</h1>
        <div className="flex items-center gap-2">
          <ViewToggle viewMode={viewMode} setViewMode={setViewMode} isBelowLg={isBelowLg} />
          <Button onClick={() => setAddPlayerDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Player
          </Button>
        </div>
      </div>

      {effectiveView === "table" ? (
        <div className="rounded-md border border-input overflow-x-auto bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Town Hall</TableHead>
                <TableHead>Trophies</TableHead>
                <TableHead>Best Trophies</TableHead>
                <TableHead>Exp. Level</TableHead>
                <TableHead>War Stars</TableHead>
                <TableHead>War League Stars</TableHead>
                <TableHead>Clan Game Points</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {players?.map((player) =>
                {
                const bestLeagueId = getLeagueIdByTrophies(player.bestTrophies);
                return (
                  <TableRow
                    key={player.playerTag}
                    className="cursor-pointer"
                    onClick={() => handleRowClick(player.playerTag)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) =>
                      {
                      if (e.key === "Enter" || e.key === " ")
                        {
                        e.preventDefault();
                        handleRowClick(player.playerTag);
                      }
                    }}
                    aria-label={`Clan connections for ${player.name ?? player.playerTag}`}>
                    <TableCell className="font-medium">{player.name ?? player.playerTag}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <img
                          src={getTownHallAssetUrl(player.townHallLevel)}
                          alt=""
                          className="h-6 w-6 object-contain"
                          onError={(e) =>
                            {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                        <span>{player.townHallLevel}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {player.league != null && (
                          <img
                            src={getLeagueAssetUrl(player.league)}
                            alt=""
                            className="h-6 w-6 object-contain"
                            onError={(e) =>
                              {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        )}
                        <span>{player.trophies}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {bestLeagueId != null && (
                          <img
                            src={getLeagueAssetUrl(bestLeagueId)}
                            alt=""
                            className="h-6 w-6 object-contain"
                            onError={(e) =>
                              {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        )}
                        <span>{player.bestTrophies ?? "–"}</span>
                      </div>
                    </TableCell>
                    <TableCell>{player.expLevel}</TableCell>
                    <TableCell>{player.warStars}</TableCell>
                    <TableCell>{player.warLeagueStars ?? "–"}</TableCell>
                    <TableCell>{player.clanGamePoints ?? "–"}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0" onClick={(e) => e.stopPropagation()}>
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="border-input" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenuItem
                            variant="destructive"
                            onClick={(e) =>
                              {
                              e.stopPropagation();
                              handleUnlinkFromUser(player.playerTag);
                            }}
                            disabled={unlinkFromUserMutation.isPending}>
                            <Trash2 className="size-4" />
                            Remove player
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div
          className="grid gap-4"
          style={{
            gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 20rem), 1fr))",
          }}>
          {players?.map((player) => (
            <PlayerOverviewCard
              key={player.playerTag}
              player={player}
              onSelect={handleRowClick}
              onUnlinkFromUser={handleUnlinkFromUser}
              isUnlinkPending={unlinkFromUserMutation.isPending}
            />
          ))}
        </div>
      )}
      <AddPlayerDialogV2 open={addPlayerDialogOpen} onOpenChange={setAddPlayerDialogOpen} />
    </div>
  );
}
